import { PERMISSIONS, type Permission } from "@/constants/permissions";

/**
 * Per-member, per-project permissions — the pure half of the access editor.
 *
 * The database is the authority (franklin-data-pipeline migration
 * `20260930101528_member_project_permissions`): `has_permission()` reads a
 * `user_project_permissions` override before the role's grant, so a Member can
 * hold more or less than the role default in any one project. This module owns
 * the rules the editor needs to present that honestly — which codes exist, what
 * each preset means, and which codes depend on which — and the wire shapes.
 */

/**
 * The codes an owner or admin may set per project. Mirrors
 * `permissions.member_configurable` in the database; the editor still renders
 * from the catalogue RPC, so a code missing from it is simply not shown.
 */
export const CONFIGURABLE_PROJECT_PERMISSIONS = [
  PERMISSIONS.ADD_PROJECT_FILES,
  PERMISSIONS.MODIFY_PROJECT_FILES,
  PERMISSIONS.DELETE_PROJECT_FILES,
  PERMISSIONS.VIEW_PM,
  PERMISSIONS.MANAGE_PM,
  PERMISSIONS.VIEW_DRAWING_COMPARE,
  PERMISSIONS.USE_MODEL_VIEWER,
  PERMISSIONS.VIEW_BRIEFS,
  PERMISSIONS.MANAGE_BRIEFS,
  PERMISSIONS.VIEW_CONTACTS,
  PERMISSIONS.MANAGE_CONTACTS,
] as const;

export type ConfigurablePermission = (typeof CONFIGURABLE_PROJECT_PERMISSIONS)[number];

const CONFIGURABLE_SET: ReadonlySet<string> = new Set(CONFIGURABLE_PROJECT_PERMISSIONS);

export function isConfigurablePermission(code: string): code is ConfigurablePermission {
  return CONFIGURABLE_SET.has(code);
}

/**
 * UI capability flags that also need a configurable code in the project.
 *
 * The app gates several project controls on role-wide flags (PROJECT_WRITE_*,
 * PROJECT_SOFT_DELETE) that the database never checks per project. Without this
 * map, switching a member's "Upload" off would leave the Upload button on and
 * the upload would fail at RLS. The mapping follows what the RLS policies
 * actually require: creating documents and folders is ADD_PROJECT_FILES, and a
 * soft delete is an UPDATE of `is_deleted`, so it is MODIFY_PROJECT_FILES —
 * DELETE_PROJECT_FILES only governs the permanent delete from the trash.
 */
export const PROJECT_FLAG_REQUIRES: Readonly<Partial<Record<Permission, ConfigurablePermission>>> =
  {
    [PERMISSIONS.PROJECT_WRITE_FILES]: PERMISSIONS.ADD_PROJECT_FILES,
    [PERMISSIONS.PROJECT_WRITE_FOLDERS]: PERMISSIONS.ADD_PROJECT_FILES,
    [PERMISSIONS.PROJECT_SOFT_DELETE]: PERMISSIONS.MODIFY_PROJECT_FILES,
  };

/** One configurable code as `get_project_permission_catalog` describes it. */
export interface PermissionCatalogEntry {
  code: ConfigurablePermission;
  /** The code this one depends on (MANAGE_PM requires VIEW_PM), or null. */
  requires: ConfigurablePermission | null;
  /** Whether the Member role grants it when no override exists. */
  roleDefault: boolean;
}

export type PermissionCatalog = readonly PermissionCatalogEntry[];

/** What a member may do in one project: the effective codes, not overrides. */
export type ProjectPermissionSet = ReadonlySet<ConfigurablePermission>;

/** The raw catalogue row, before it is trusted. */
export interface PermissionCatalogRow {
  code: string;
  requires_code: string | null;
  role_default: boolean;
}

/**
 * Keep only rows naming a code this build knows how to present, so a code the
 * database adds later is ignored rather than rendered as a nameless switch.
 */
export function parsePermissionCatalog(rows: readonly PermissionCatalogRow[]): PermissionCatalog {
  return rows
    .filter((row) => isConfigurablePermission(row.code))
    .map((row) => ({
      code: row.code as ConfigurablePermission,
      requires:
        row.requires_code && isConfigurablePermission(row.requires_code) ? row.requires_code : null,
      roleDefault: row.role_default,
    }));
}

export function roleDefaultSet(catalog: PermissionCatalog): Set<ConfigurablePermission> {
  return new Set(catalog.filter((entry) => entry.roleDefault).map((entry) => entry.code));
}

export type AccessPreset = "view" | "contributor" | "full" | "custom";

/**
 * The drawing and model viewers are on in every preset: they only open what
 * the member can already see, so even View only includes them.
 */
export const VIEWER_PERMISSIONS: readonly ConfigurablePermission[] = [
  PERMISSIONS.VIEW_DRAWING_COMPARE,
  PERMISSIONS.USE_MODEL_VIEWER,
];

/** Presets in the order they are offered and matched. */
export const ACCESS_PRESETS: readonly Exclude<AccessPreset, "custom">[] = [
  "view",
  "contributor",
  "full",
];

/**
 * The codes a preset switches on.
 *
 * - view: every code some other code depends on — the "see it" half of each
 *   view/manage pair — plus the viewers, and nothing that writes.
 * - contributor: the Member role's default, read from the catalogue so it can
 *   never drift from what a member with no overrides actually holds.
 * - full: everything configurable.
 */
export function presetPermissions(
  preset: Exclude<AccessPreset, "custom">,
  catalog: PermissionCatalog
): Set<ConfigurablePermission> {
  switch (preset) {
    case "view": {
      const seen = new Set<ConfigurablePermission>(VIEWER_PERMISSIONS);
      for (const entry of catalog) if (entry.requires) seen.add(entry.requires);
      return new Set(catalog.map((entry) => entry.code).filter((code) => seen.has(code)));
    }
    case "contributor":
      return roleDefaultSet(catalog);
    case "full":
      return new Set(catalog.map((entry) => entry.code));
  }
}

/** The set a project stands at: its own entry, or the role default. */
export function permissionsFor(
  state: {
    catalog: PermissionCatalog;
    byProject: ReadonlyMap<number, ProjectPermissionSet>;
  },
  projectId: number
): ProjectPermissionSet {
  return state.byProject.get(projectId) ?? presetPermissions("contributor", state.catalog);
}

export function samePermissions(a: ProjectPermissionSet, b: ProjectPermissionSet): boolean {
  return a.size === b.size && [...a].every((code) => b.has(code));
}

/** The first preset the set matches, or "custom". */
export function detectPreset(set: ProjectPermissionSet, catalog: PermissionCatalog): AccessPreset {
  for (const preset of ACCESS_PRESETS) {
    if (samePermissions(set, presetPermissions(preset, catalog))) return preset;
  }
  return "custom";
}

/**
 * Switch one code, keeping the set coherent with the dependency rules the
 * database enforces: turning a code on turns on what it requires, turning a
 * code off turns off everything that requires it. The save RPC refuses an
 * incoherent map, so the editor must never be able to build one.
 */
function enablePermissionDependencies(
  next: Set<ConfigurablePermission>,
  code: ConfigurablePermission,
  catalog: PermissionCatalog
): void {
  const byCode = new Map(catalog.map((entry) => [entry.code, entry]));
  let cursor: ConfigurablePermission | null = code;
  while (cursor && !next.has(cursor)) {
    next.add(cursor);
    cursor = byCode.get(cursor)?.requires ?? null;
  }
}

export function togglePermission(
  set: ProjectPermissionSet,
  code: ConfigurablePermission,
  on: boolean,
  catalog: PermissionCatalog
): Set<ConfigurablePermission> {
  const next = new Set(set);
  if (on) {
    enablePermissionDependencies(next, code, catalog);
    return next;
  }
  const queue: ConfigurablePermission[] = [code];
  for (let current = queue.pop(); current !== undefined; current = queue.pop()) {
    if (!next.delete(current)) continue;
    for (const entry of catalog) {
      if (entry.requires === current) queue.push(entry.code);
    }
  }
  return next;
}

/** The code a switch depends on being on, when it is currently off. */
export function blockingRequirement(
  set: ProjectPermissionSet,
  code: ConfigurablePermission,
  catalog: PermissionCatalog
): ConfigurablePermission | null {
  const requires = catalog.find((entry) => entry.code === code)?.requires ?? null;
  return requires && !set.has(requires) ? requires : null;
}

/** `{"<project_id>": {"<CODE>": bool}}` — what the save RPC and the invite take. */
export type ProjectPermissionsPayload = Record<string, Record<string, boolean>>;

/**
 * The complete state of each named project. The RPC treats a project's map as
 * its whole override state (an omitted code returns to the default) and stores
 * only what differs from the default, so sending every code is both correct and
 * what keeps a later change to the role default flowing to members nobody has
 * customised.
 */
export function buildProjectPermissionsPayload(
  byProject: ReadonlyMap<number, ProjectPermissionSet>,
  projectIds: Iterable<number>,
  catalog: PermissionCatalog
): ProjectPermissionsPayload {
  const payload: ProjectPermissionsPayload = {};
  for (const projectId of projectIds) {
    const set = byProject.get(projectId);
    if (!set) continue;
    const codes: Record<string, boolean> = {};
    for (const entry of catalog) codes[entry.code] = set.has(entry.code);
    payload[String(projectId)] = codes;
  }
  return payload;
}

/** One row of `org_get_member_project_access`. */
export interface MemberProjectAccessRow {
  project_id: number;
  permission_code: string;
  role_default: boolean;
  override_granted: boolean | null;
  effective: boolean;
}

/** Effective codes per assigned project, as the editor seeds from them. */
export function effectivePermissionsByProject(
  rows: readonly MemberProjectAccessRow[]
): Map<number, Set<ConfigurablePermission>> {
  const byProject = new Map<number, Set<ConfigurablePermission>>();
  for (const row of rows) {
    const set = byProject.get(row.project_id) ?? new Set<ConfigurablePermission>();
    if (row.effective && isConfigurablePermission(row.permission_code)) {
      set.add(row.permission_code);
    }
    byProject.set(row.project_id, set);
  }
  return byProject;
}

/**
 * The stored overrides as one comparable string — what `hasScopeMoved` checks,
 * so a change another admin made to this member's permissions under an open
 * dialog refuses the save instead of being overwritten.
 */
export function overrideSignature(rows: readonly MemberProjectAccessRow[]): string {
  return rows
    .filter((row) => row.override_granted !== null)
    .map((row) => `${row.project_id}:${row.permission_code}:${row.override_granted ? 1 : 0}`)
    .sort()
    .join("|");
}

/** Groups the editor renders, in order. Codes absent from the catalogue are dropped. */
export const PERMISSION_GROUPS = [
  {
    key: "files",
    codes: [
      PERMISSIONS.ADD_PROJECT_FILES,
      PERMISSIONS.MODIFY_PROJECT_FILES,
      PERMISSIONS.DELETE_PROJECT_FILES,
    ],
  },
  { key: "projectManagement", codes: [PERMISSIONS.VIEW_PM, PERMISSIONS.MANAGE_PM] },
  { key: "viewers", codes: [PERMISSIONS.VIEW_DRAWING_COMPARE, PERMISSIONS.USE_MODEL_VIEWER] },
  { key: "brief", codes: [PERMISSIONS.VIEW_BRIEFS, PERMISSIONS.MANAGE_BRIEFS] },
  { key: "contacts", codes: [PERMISSIONS.VIEW_CONTACTS, PERMISSIONS.MANAGE_CONTACTS] },
] as const satisfies readonly { key: string; codes: readonly ConfigurablePermission[] }[];

export type PermissionGroupKey = (typeof PERMISSION_GROUPS)[number]["key"];

/**
 * Is a project-scoped check answerable from the per-project effective set, and
 * if so, what does it say? `null` means "not a per-project question — use the
 * role path". Lives here so usePermissions and its tests share one rule.
 */
export function projectScopedAnswer(
  permission: Permission,
  projectId: number,
  projectPermissions: ReadonlyMap<number, ProjectPermissionSet>
): boolean | null {
  const required = isConfigurablePermission(permission)
    ? permission
    : PROJECT_FLAG_REQUIRES[permission];
  if (!required) return null;
  return projectPermissions.get(projectId)?.has(required) ?? false;
}
